import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const CONSENT_STORAGE_KEY = "dwik-cookie-consent-v1";

export interface ConsentChoices {
  analytics: boolean;
  marketing: boolean;
}

interface StoredConsent extends ConsentChoices {
  version: 1;
  updatedAt: string;
}

interface ConsentContextValue {
  consent: ConsentChoices | null;
  preferencesOpen: boolean;
  acceptAll: () => void;
  rejectOptional: () => void;
  savePreferences: (choices: ConsentChoices) => void;
  openPreferences: () => void;
  closePreferences: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readStoredConsent(): ConsentChoices | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredConsent>;
    if (
      stored.version !== 1 ||
      typeof stored.analytics !== "boolean" ||
      typeof stored.marketing !== "boolean"
    ) {
      return null;
    }
    return { analytics: stored.analytics, marketing: stored.marketing };
  } catch {
    return null;
  }
}

function persistConsent(choices: ConsentChoices): void {
  const stored: StoredConsent = {
    version: 1,
    updatedAt: new Date().toISOString(),
    ...choices,
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentChoices | null>(() =>
    readStoredConsent(),
  );
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const savePreferences = useCallback((choices: ConsentChoices) => {
    persistConsent(choices);
    setConsent(choices);
    setPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => savePreferences({ analytics: true, marketing: true }),
    [savePreferences],
  );
  const rejectOptional = useCallback(
    () => savePreferences({ analytics: false, marketing: false }),
    [savePreferences],
  );
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      preferencesOpen,
      acceptAll,
      rejectOptional,
      savePreferences,
      openPreferences,
      closePreferences,
    }),
    [
      consent,
      preferencesOpen,
      acceptAll,
      rejectOptional,
      savePreferences,
      openPreferences,
      closePreferences,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const value = useContext(ConsentContext);
  if (!value) throw new Error("useConsent must be used within ConsentProvider");
  return value;
}
